import {
  Group,
  Flex,
  Button,
  useMantineTheme,
  Title,
  Text,
  ActionIcon,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useContext, useEffect, useState, memo } from "react";
import { MdClose } from "react-icons/md";
import { AppContext } from "src/api/AppContext";
import FormCard from "src/components/FormCard";
import MultiSelect from "src/components/MultiSelect";
import NumberInput from "src/components/NumberInput/NumberInput";
import Select, { ISelect } from "src/components/Select";
import Switch from "src/components/Switch/Switch";
import TextInput from "src/components/TextInput/TextInput";
import {
  CategoryOptionType,
  CreateRecurringChargeInput,
  useGetCategoryOptionManyLazyQuery,
  useGetLocationTaxRateNamesQuery,
  useCreateScheduledChargesMutation,
  useUpdateScheduledChargesMutation,
  GenericConnectInput,
  CreateScheduledChargesInput,
  UpdateScheduledChargesInput,
  ScheduledChargesByTransactionIdQuery,
} from "src/generated/graphql";
import { CRUDSTATES } from "src/utils/constants";
import { object, string } from "yup";
// import { ICharge } from ".";

interface IScheduleChargesProps {
  state: CRUDSTATES;
  charge?: IFormProps;
  scheduleTransactionId: string;
  subscriptionId?: string;
  setIsCharge?: (e: boolean) => void;
  onRefetchCharges: () => void;
  closeModal: () => void;
}

export type IFormProps =
  // Omit<UpdateScheduledChargesInput, "revenueCategory"> &
  {
    id?: string;
    name: string;
    cost: number;
    isPostTax: boolean;
    isTaxable: boolean;
    taxRates?: string[];
    categoryOption: string;
  };

const ScheduledCharge = ({
  state,
  charge,
  scheduleTransactionId,
  subscriptionId,
  setIsCharge,
  onRefetchCharges,
  closeModal,
}: IScheduleChargesProps) => {
  const theme = useMantineTheme();
  const { currentOrganization, currentLocation } = useContext(AppContext);
  const [revenueArr, setrevenueArr] = useState<ISelect[]>([]);

  const [getCategories] = useGetCategoryOptionManyLazyQuery({
    onCompleted(data) {
      let _data = data?.categoryOptionMany;
      let _dataRevenue =
        _data
          ?.filter((e) => e.type === CategoryOptionType.Revenue)
          .map((val) => ({
            value: val.id,
            label: val.name,
          })) || [];
      setrevenueArr(_dataRevenue);
    },
  });

  const { data: taxRates } = useGetLocationTaxRateNamesQuery({
    variables: { locationId: currentLocation?.id },
    skip: !currentLocation?.id,
  });

  const [createScheduleCharge, { loading: createLoading }] =
    useCreateScheduledChargesMutation({
      onCompleted: (res) => {
        onRefetchCharges();
        setIsCharge && setIsCharge(false);
      },
    });

  const [updateScheduleCharge, { loading: updateLoading }] =
    useUpdateScheduledChargesMutation({
      onCompleted: (res) => {
        onRefetchCharges();
        setIsCharge && setIsCharge(false);
      },
    });

  useEffect(() => {
    if (currentOrganization?.id) {
      getCategories({
        variables: { orgId: currentOrganization?.id || "" },
      });
    }
  }, [currentOrganization]);

  const chargeInitialValues = {
    name: charge ? charge.name : "",
    cost: charge ? charge.cost : 0,
    isTaxable: charge ? charge.isTaxable : false,
    isPostTax: charge ? charge.isPostTax : false,
    taxRates: charge?.taxRates ? charge.taxRates : [],
    categoryOption: charge ? charge.categoryOption : "",
  };

  const chargeFormSchema = object().shape({
    categoryOption: string().required("Revenue Category is required"),
    cost: string().required("Cost is required"),
    name: string()
      .trim("No spaces allowed")
      .strict(true)
      .min(2, "Name should must be least 2 letters")
      .max(30, "Name must not be greater than 30 letters")
      .required("Name is required"),
  });

  const form = useForm<IFormProps>({
    initialValues: chargeInitialValues,
    validate: yupResolver(chargeFormSchema),
  });

  const handleCreateSchedulePayment = (values: IFormProps) => {
    const { taxRates, categoryOption, ...rest } = values;

    let _obj = {
      ...rest,
      ...(subscriptionId ? { subscription: { id: subscriptionId } } : {}),
      ...{ scheduledTransaction: { id: scheduleTransactionId } },
      ...{ revenueCategory: { id: categoryOption } },
      ...{
        taxRates: (taxRates && taxRates.map((e) => ({ id: e }))) || [],
      },
    };

    if (charge && charge.id) {
      updateScheduleCharge({
        variables: {
          id: charge.id,
          data: _obj,
        },
      });
    } else {
      createScheduleCharge({
        variables: {
          data: _obj,
        },
      });
    }
  };

  return (
    <>
      <Flex justify={"space-between"} mb={15}>
        <Text size={20} fw={500}>
          {charge ? "Update" : "Create"} Charge
        </Text>
        <ActionIcon
          size="lg"
          onClick={() => {
            setIsCharge && state === CRUDSTATES.Create
              ? setIsCharge(false)
              : closeModal();
          }}
        >
          <MdClose size={20} />
        </ActionIcon>
      </Flex>

      <FormCard
        title={`${state} Charge`}
        onSave={form.onSubmit((values) => handleCreateSchedulePayment(values))}
        hideCard={true}
        mb={10}
        size="md"
        radius={4}
      >
        <>
          <Group position="apart">
            <Title size={20} weight={400}>
              General
            </Title>

            <Switch
              labelPosition="left"
              label="Taxable"
              checked={!!form.getInputProps("isTaxable")?.value}
              {...form.getInputProps("isTaxable")}
              mt={"sm"}
            />
          </Group>

          <TextInput
            mt={"xs"}
            withAsterisk
            label="Name"
            {...form.getInputProps("name")}
          />

          <Group position="apart">
            <NumberInput
              withAsterisk
              label="Cost"
              precision={2}
              {...form.getInputProps("cost")}
            />

            {/* {isTaxable && ( */}
            <Switch
              labelPosition="left"
              label="Value is Post-Tax"
              checked={!!form.getInputProps("isPostTax")?.value}
              {...form.getInputProps("isPostTax")}
              mt={"sm"}
            />
            {/* )} */}
          </Group>

          {/* {isTaxable && ( */}
          <MultiSelect
            label="Tax Rates"
            data={
              taxRates?.locationTaxRateMany?.map((val) => ({
                value: val.id,
                label: val.organizationTaxRate.name,
              })) || []
            }
            {...form.getInputProps("taxRates")}
            styles={{
              input: {
                borderRadius: "5px !important",
              },
            }}
          />
          {/* )} */}

          <Select
            withAsterisk
            data={revenueArr}
            label="Revenue Category"
            {...form.getInputProps("categoryOption")}
          />

          <Group position="apart" mt="md" style={{ marginTop: 30 }}>
            <Button
              radius="md"
              variant="outline"
              onClick={() => {
                setIsCharge && setIsCharge(false);
              }}
              style={{
                flex: 1,
                color: theme.colors.secondaryTextColor[6],
              }}
              sx={{
                backgroundColor: theme.colors.secondary[6],
                "&:hover": {
                  background: theme.colors.secondary[5],
                },
              }}
            >
              Cancel
            </Button>
            <Button
              radius="md"
              type="submit"
              // onClick={handleCreateSchedulePayment}
              style={{
                flex: 1,
              }}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
            >
              Save
            </Button>
          </Group>
        </>
      </FormCard>
    </>
  );
};

export default ScheduledCharge;
